import { useCallback, useEffect, useRef, useState, RefObject } from 'react';

export interface TextSelection {
  text: string;
  rect: DOMRect;
}

interface UseTextSelectionResult {
  selection: TextSelection | null;
  clear: () => void;
}

const MIN_SELECTION_CHARS = 3;
/** How long a touch selection must hold still before the menu appears. */
const TOUCH_SETTLE_MS = 300;

/** A finger, not a mouse: phones and tablets. */
export function isCoarsePointer(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * The current document selection, if it is long enough and lies wholly
 * inside `container` - selections in the sidebar, header, etc. are ignored.
 */
function readSelectionIn(container: HTMLElement | null): TextSelection | null {
  if (!container) return null;
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const text = sel.toString().trim();
  if (text.length < MIN_SELECTION_CHARS) return null;
  const range = sel.getRangeAt(0);
  if (
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  ) {
    return null;
  }
  const rect = range.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return { text, rect };
}

/**
 * Tracks the user's text selection inside a container. Captures the selected
 * text + bounding rect so a floating action menu can anchor itself above it.
 *
 * Listens at the document level and reads `containerRef.current` at event
 * time rather than at mount time - so it works even when the container is
 * conditionally rendered (e.g. mounted only after data loads).
 *
 * Two triggers. A mouse finishes a selection with `mouseup`. A finger does
 * not - long-press selection on a phone fires no `mouseup` at all, so the
 * menu (and with it "Explain") never appeared there. On a coarse pointer the
 * hook also follows `selectionchange`, waiting for the handles to settle.
 *
 * Selection is cleared on Escape or on mousedown outside the popover so the
 * menu doesn't linger when the user moves on.
 */
export function useTextSelection(
  containerRef: RefObject<HTMLElement | null>
): UseTextSelectionResult {
  const [selection, setSelection] = useState<TextSelection | null>(null);
  // Keep a ref so the document-level listeners can read fresh state without
  // re-binding on every render.
  const selectionRef = useRef<TextSelection | null>(null);
  selectionRef.current = selection;

  const clear = useCallback(() => {
    setSelection(null);
  }, []);

  // Document-level mouseup. We read containerRef.current inside the handler
  // so a late-mounted container is picked up the first time the user
  // selects something.
  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Ignore mouseups that started inside the popover itself.
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-selection-popover]')) return;

      requestAnimationFrame(() => {
        setSelection(readSelectionIn(containerRef.current));
      });
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [containerRef]);

  // Touch: follow the selection itself, debounced so the menu does not chase
  // the handles while the user drags them.
  useEffect(() => {
    if (!isCoarsePointer()) return;
    let timer: number | undefined;
    const handleChange = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setSelection(readSelectionIn(containerRef.current));
      }, TOUCH_SETTLE_MS);
    };
    document.addEventListener('selectionchange', handleChange);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('selectionchange', handleChange);
    };
  }, [containerRef]);

  // Dismiss on Escape or on mousedown outside the popover.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectionRef.current) clear();
    };
    const handleDocMouseDown = (e: MouseEvent) => {
      if (!selectionRef.current) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-selection-popover]')) return;
      clear();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleDocMouseDown);
    };
  }, [clear]);

  return { selection, clear };
}
